import { useAuth } from "@core/contexts/AuthContext";
import { Poll } from "@core/lib/pollStorage";
import { getDeviceId } from "@core/lib/guestIdentityService";
import { useTranslation } from "react-i18next";

export type ResultsAccessStatus =
  | { allowed: true; reason?: never; message?: never }
  | {
      allowed: false;
      reason: "not-creator" | "not-voted" | "restricted";
      message: string;
    };

/**
 * Hook pour vérifier l'accès aux résultats d'un sondage
 * Basé sur le paramètre `resultsVisibility` dans les settings
 */
export function useResultsAccess(
  poll: Poll | null,
  hasVoted: boolean = false,
): ResultsAccessStatus {
  const { user } = useAuth();
  const { t } = useTranslation("common");

  if (!poll) {
    return { allowed: true }; // Pas de poll = pas de restriction (loading state)
  }

  // Récupérer la visibilité des résultats (défaut: public).
  // SÉCURITÉ (IDOR, cf. #828) : le loader form-polls place resultsVisibility en
  // top-level sur le poll, tandis que date-polls l'a dans settings. On lit les
  // DEUX — sinon settings.resultsVisibility est undefined pour les form-polls →
  // défaut "public" → fuite des résultats creator-only/voters.
  const visibility =
    (poll as { resultsVisibility?: string }).resultsVisibility ||
    (poll.settings as { resultsVisibility?: string })?.resultsVisibility ||
    "public";

  // Mode "Public" : tout le monde peut voir
  if (visibility === "public") {
    return { allowed: true };
  }

  // Vérifier si l'utilisateur est le créateur. Deux cas :
  // - poll avec creator_id (user authentifié, ou device id legacy) : match direct.
  // - poll invité (creator_id NULL) : l'identité créateur = guest_fingerprint,
  //   que le RPC get_poll_by_slug ne RÉVÈLE QU'au vrai créateur (caller
  //   fingerprint matchant). Sa simple présence sur le poll chargé suffit donc
  //   à prouver que l'appelant courant est le créateur.
  const currentDeviceId = getDeviceId();
  const guestFingerprint = (poll as { guest_fingerprint?: string | null })
    .guest_fingerprint;
  // NB : certains adaptateurs (FormPollResults.adaptedPoll) forcent creator_id à
  // "unknown" pour les invités — on le traite comme absent pour ne pas court-
  // circuiter la branche invité (reconnaissance via guest_fingerprint révélé).
  const realCreatorId =
    poll.creator_id && poll.creator_id !== "unknown" ? poll.creator_id : null;
  const isCreator = realCreatorId
    ? user?.id === realCreatorId || realCreatorId === currentDeviceId
    : !!guestFingerprint;

  // Mode "Créateur uniquement" + "restricted"
  // 'restricted' est la visibilité par défaut des form-polls : doit être
  // traitée comme creator-only (sinon fallthrough → accès public, fuite).
  if (visibility === "creator-only" || visibility === "restricted") {
    if (!isCreator) {
      // Message différent selon si l'utilisateur est connecté ou non
      if (!user && !currentDeviceId) {
        return {
          allowed: false,
          reason: "restricted",
          message: t("results_access.creator_only_login"),
        };
      }
      return {
        allowed: false,
        reason: "not-creator",
        message: t("results_access.creator_only"),
      };
    }
    return { allowed: true };
  }

  // Mode "Participants après vote"
  if (visibility === "voters") {
    // Le créateur peut toujours voir
    if (isCreator) {
      return { allowed: true };
    }

    // Les autres doivent avoir voté
    if (!hasVoted) {
      return {
        allowed: false,
        reason: "not-voted",
        message: t("results_access.must_vote"),
      };
    }

    return { allowed: true };
  }

  // Fallback: autoriser l'accès par défaut
  return { allowed: true };
}
